// frontend/src/components/CustomerContractList.js
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Grid,
  MenuItem,
  IconButton,
  Typography,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import dayjs from 'dayjs';
import api from '../api';

import CONTRACT_STATUS_OPTIONS from '../config/contractStatusOptions';
import CONTRACT_TYPE_OPTIONS from '../config/contractTypeOptions';

export default function CustomerContractList() {
  const [contracts, setContracts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);

  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);

  const [form, setForm] = useState({
    customerId: '',
    projectId: '',
    title: '',
    type: '',
    typeOther: '',
    status: '',
    amount: '',
    startDate: '',
    endDate: '',
    renewalDate: '',
    memo: '',
  });

  /* =====================
   * load masters
   * ===================== */
  const loadMasters = useCallback(async () => {
    const [cRes, pRes] = await Promise.all([
      api.get('/api/customers'),
      api.get('/api/projects'),
    ]);
    setCustomers(Array.isArray(cRes.data) ? cRes.data : []);
    setProjects(Array.isArray(pRes.data) ? pRes.data : []);
  }, []);

  /* =====================
   * load contracts
   * ===================== */
  const loadContracts = useCallback(async () => {
    const res = await api.get('/api/contracts');
    const list = Array.isArray(res.data) ? res.data : [];

    const joined = list.map((c) => {
      const customer = customers.find((x) => Number(x.id) === Number(c.customerId));
      const project = projects.find((x) => Number(x.id) === Number(c.projectId));

      return {
        ...c,
        id: Number(c.id),
        customerId: Number(c.customerId),
        projectId: Number(c.projectId),
        customerName: customer?.name || c.customerName || '',
        projectTitle: project?.title || c.projectTitle || '',
        typeLabel:
          CONTRACT_TYPE_OPTIONS.find((o) => o.value === c.type)?.label || c.type || '',
        statusLabel:
          CONTRACT_STATUS_OPTIONS.find((o) => o.value === c.status)?.label ||
          c.status ||
          '',
        startDate: c.startDate ? dayjs(c.startDate).format('YYYY-MM-DD') : '',
        endDate: c.endDate ? dayjs(c.endDate).format('YYYY-MM-DD') : '',
        renewalDate: c.renewalDate ? dayjs(c.renewalDate).format('YYYY-MM-DD') : '',
      };
    });

    setContracts(joined);
  }, [customers, projects]);

  useEffect(() => {
    loadMasters();
  }, [loadMasters]);

  useEffect(() => {
    if (customers.length || projects.length) {
      loadContracts();
    }
  }, [customers, projects, loadContracts]);

  /* =====================
   * handlers
   * ===================== */
  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => {
    setEditId(null);
    setSelectedCustomer(null);
    setSelectedProject(null);
    setForm({
      customerId: '',
      projectId: '',
      title: '',
      type: '',
      typeOther: '',
      status: '',
      amount: '',
      startDate: '',
      endDate: '',
      renewalDate: '',
      memo: '',
    });
  };

  const openNewDialog = () => {
    resetForm();
    setOpen(true);
  };

  const handleEdit = (row) => {
    setEditId(row.id);
    setForm({
      customerId: row.customerId ?? '',
      projectId: row.projectId ?? '',
      title: row.title || '',
      type: row.type || '',
      typeOther: row.typeDetail || row.typeOther || '',
      status: row.status || '',
      amount: row.amount ?? '',
      startDate: row.startDate || '',
      endDate: row.endDate || '',
      renewalDate: row.renewalDate || '',
      memo: row.memo || '',
    });

    setSelectedCustomer(customers.find(c => Number(c.id) === Number(row.customerId)) || null);
    setSelectedProject(projects.find(p => Number(p.id) === Number(row.projectId)) || null);
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('本当に削除しますか？')) return;
    await api.delete(`/api/contracts/${id}`);
    loadContracts();
  };

  const handleSubmit = async () => {
    if (!form.customerId || !form.projectId || !form.title) {
      alert('クライアント・案件・契約名は必須です');
      return;
    }
    if (!form.type) {
      alert('契約種類は必須です');
      return;
    }
    if (!form.status) {
      alert('ステータスは必須です');
      return;
    }
    if (form.type === 'other' && !form.typeOther.trim()) {
      alert('「その他」を選んだ場合は、契約種類（自由入力）を入力してください');
      return;
    }

    const payload = {
      customerId: Number(form.customerId),
      projectId: Number(form.projectId),
      title: form.title,
      type: form.type,
      typeDetail: form.type === 'other' ? form.typeOther : null,
      status: form.status,
      amount: form.amount !== '' && form.amount != null ? Number(form.amount) : null,
      startDate: form.startDate || null,
      endDate: form.endDate || null,
      renewalDate: form.renewalDate || null,
      memo: form.memo || null,
    };

    editId
      ? await api.put(`/api/contracts/${editId}`, payload)
      : await api.post('/api/contracts', payload);

    setOpen(false);
    resetForm();
    loadContracts();
  };

  /* =====================
   * DataGrid columns（横暴走対策のみ）
   * ===================== */
  const columns = [
    { field: 'customerName', headerName: 'クライアント', flex: 1.2, minWidth: 140 },
    { field: 'projectTitle', headerName: '案件', flex: 1.4, minWidth: 160 },
    { field: 'title', headerName: '契約名', flex: 1.4, minWidth: 160 },
    { field: 'typeLabel', headerName: '種類', width: 130 },
    { field: 'statusLabel', headerName: 'ステータス', width: 140 },
    {
      field: 'amount',
      headerName: '契約金額',
      flex: 1,
      minWidth: 130,
      maxWidth: 160,
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%', justifyContent: 'flex-end', width: '100%' }}>
          {p.value != null ? `¥${Number(p.value).toLocaleString()}` : '-'}
        </Box>
      ),
    },
    { field: 'startDate', headerName: '開始日', width: 110 },
    { field: 'endDate', headerName: '終了日', width: 110 },
    // 更新日は ID と同じく表示優先度低、削除して横幅節約
    {
      field: 'actions',
      headerName: '',
      flex: 0.6,
      minWidth: 90,
      maxWidth: 120,
      sortable: false,
      renderCell: (p) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
          <IconButton size="small" onClick={() => handleEdit(p.row)}>
            <EditIcon />
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(p.row.id)}>
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button variant="contained" onClick={openNewDialog}>
          新規契約を追加
        </Button>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          ※ 一覧の「クライアント」「案件」はマスタ結合で表示します
        </Typography>
      </Box>

      {/* 横暴走防止ラッパー */}
      <Box sx={{ width: '100%' }}>
        <Box sx={{ width: '100%' }}>
          <DataGrid
            rows={contracts}
            columns={columns}
            autoHeight
            initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            getRowHeight={() => 44}
            slots={{ toolbar: GridToolbar }}
            slotProps={{ toolbar: { showQuickFilter: true } }}
          />
        </Box>
      </Box>

      {/* ===== Dialog（モーダル完全復元） ===== */}
      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{editId ? '契約を編集' : '契約を登録'}</DialogTitle>

        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {/* Row1 */}
            <Grid container spacing={2}>
              <Grid item size={4}>
                <Autocomplete
                  options={customers}
                  getOptionLabel={(o) => o?.name || ''}
                  value={selectedCustomer}
                  isOptionEqualToValue={(o, v) => Number(o?.id) === Number(v?.id)}
                  onChange={(_, v) => {
                    setSelectedCustomer(v || null);
                    setSelectedProject(null);
                    setForm({ ...form, customerId: v ? v.id : '', projectId: '' });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="クライアント" />
                  )}
                />
              </Grid>

              <Grid item size={4}>
                <Autocomplete
                  options={
                    form.customerId
                      ? projects.filter((p) => Number(p.customerId) === Number(form.customerId))
                      : projects
                  }
                  getOptionLabel={(o) => o?.title || ''}
                  value={selectedProject}
                  isOptionEqualToValue={(o, v) => Number(o?.id) === Number(v?.id)}
                  onChange={(_, v) => {
                    setSelectedProject(v || null);
                    setForm({ ...form, projectId: v ? v.id : '' });
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="案件" />
                  )}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2} marginTop={2}>
              <Grid item size={6}>
                <TextField
                  fullWidth
                  label="契約名"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item size={6}>
                <TextField
                  fullWidth
                  label="契約金額 (円)"
                  name="amount"
                  type="number"
                  value={form.amount}
                  onChange={handleChange}
                  inputProps={{ min: 0, step: 1 }}
                />
              </Grid>
            </Grid>

            {/* Row2 */}
            <Grid container spacing={2} marginTop={2}>
              <Grid item size={6}>
                <TextField
                  select
                  fullWidth
                  label="契約種類"
                  name="type"
                  value={form.type}
                  onChange={(e) => {
                    const v = e.target.value;
                    setForm({ ...form, type: v, typeOther: v === 'other' ? form.typeOther : '' });
                  }}
                >
                  {CONTRACT_TYPE_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item size={6}>
                <TextField
                  select
                  fullWidth
                  label="ステータス"
                  name="status"
                  value={form.status}
                  onChange={handleChange}
                >
                  {CONTRACT_STATUS_OPTIONS.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              {form.type === 'other' && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="契約種類（自由入力）"
                    name="typeOther"
                    value={form.typeOther}
                    onChange={handleChange}
                  />
                </Grid>
              )}
            </Grid>

            {/* Row3 */}
            <Grid container spacing={2} marginTop={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="開始日"
                  name="startDate"
                  InputLabelProps={{ shrink: true }}
                  value={form.startDate}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="終了日"
                  name="endDate"
                  InputLabelProps={{ shrink: true }}
                  value={form.endDate}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  type="date"
                  label="更新日"
                  name="renewalDate"
                  InputLabelProps={{ shrink: true }}
                  value={form.renewalDate}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            {/* memo */}
            <Grid container spacing={2} marginTop={2}>
              <Grid item size={12}>
                <TextField
                  fullWidth
                  multiline
                  minRows={3}
                  label="備考"
                  name="memo"
                  value={form.memo}
                  onChange={handleChange}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root.MuiInputBase-multiline': {
                      height: 'auto',
                      alignItems: 'flex-start',
                    },
                  }}
                />



              </Grid>
            </Grid>

          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)}>キャンセル</Button>
          <Button onClick={handleSubmit} variant="contained">
            {editId ? '更新' : '登録'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
